import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    WritableSignal,
    inject,
    signal
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';

import { BarcodeScannerDialog } from '../../../../components/dialogs/barcode-scanner/barcode-scanner.dialog';
import { Tools } from '../../../../core/utils/tools';
import { GateGmShareholderModel } from '../../../../models/gate-gm-shareholder.model';
import { OptionalType } from '../../../../models/types/optional.type';
import { ErrorsService } from '../../../../services/errors.service';
import { GateResource } from '../../../../services/resources/gate.resource';
import { RegisterProxiesResultModel } from './models/register-proxy-result.model';

@Component({
    selector: 'sl-gate-register-proxies',
    templateUrl: './gate-register-proxies.dialog.html',
    styleUrls: ['./gate-register-proxies.dialog.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GateRegisterProxiesDialog implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('barcodeInput', { static: true }) barcodeInput: ElementRef<HTMLInputElement>;

    state = {
        representativeShareholder: signal(undefined) as WritableSignal<OptionalType<GateGmShareholderModel>>,
        proxies: signal([]) as WritableSignal<GateGmShareholderModel[]>
    };

    hasMediaDevices = false;
    proxyBarcode = '';
    isLoading = signal(false);

    private readonly matDialog = inject(MatDialog);
    private readonly dialogRef = inject<MatDialogRef<GateRegisterProxiesDialog, RegisterProxiesResultModel>>(MatDialogRef);
    private readonly data = inject<{ representativeBarcode: string }>(MAT_DIALOG_DATA);
    private readonly errorsService = inject(ErrorsService);
    private readonly gateResource = inject(GateResource);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.hasMediaDevices = navigator?.mediaDevices !== undefined;
        this.errorsService.shouldDisplayAlertOnError = true;

        this.setRepresentative(this.data.representativeBarcode);
    }

    ngAfterViewInit(): void {
        Tools.Utils.startFocusTrap(this.barcodeInput.nativeElement, this.changeDetectorRef);
    }

    ngOnDestroy(): void {
        this.errorsService.shouldDisplayAlertOnError = false;
    }

    openBarcodeScanDialog(action: (barcode: string) => void): void {
        const dialogRef = this.matDialog.open(BarcodeScannerDialog, {
            width: '500px',
            height: '500px',
            maxWidth: '98vw',
            maxHeight: '98svh'
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!!result) {
                action(result);
            }
        });
    }

    addProxy(barcode: string): void {
        if (this.isLoading()) {
            return;
        }

        this.isLoading.set(true);
        this.gateResource
            .getShareholderByBarcode(barcode)
            .subscribe(result => {
                if (
                    // eslint-disable-next-line eqeqeq
                    this.state.representativeShareholder()?.shareholderId == result.value.shareholderId
                    // eslint-disable-next-line eqeqeq
                    || !!this.state.proxies().find(s => s.shareholderId == result.value.shareholderId)
                ) {
                    return;
                }

                this.state.proxies.update(curr => [...curr, result.value]);
            })
            .add(() => {
                this.proxyBarcode = '';
                this.isLoading.set(false);
            });
    }

    removeProxy(shareholderId: number): void {
        // eslint-disable-next-line eqeqeq
        this.state.proxies.update(val => val.filter(s => s.shareholderId != shareholderId));
    }

    submit(): void {
        const representativeShareholderId = this.state.representativeShareholder()?.shareholderId;
        if (!representativeShareholderId) {
            return;
        }

        const proxyIds = this.state.proxies().map(p => p.shareholderId);
        this.gateResource.registerProxies(representativeShareholderId, proxyIds).subscribe(result =>
            this.dialogRef.close({
                representativeShareholderId,
                proxyResults: result.value
            })
        );
    }

    private setRepresentative(barcode: string): void {
        if (this.isLoading()) {
            return;
        }

        Tools.Utils.endFocusTrap(this.barcodeInput.nativeElement, this.changeDetectorRef);
        this.changeDetectorRef.detectChanges();
        this.isLoading.set(true);
        this.gateResource
            .getShareholderByBarcode(barcode)
            .subscribe(result => this.state.representativeShareholder.set(result.value))
            .add(() => {
                if (!this.state.representativeShareholder()) {
                    this.dialogRef.close();
                    return;
                }

                this.isLoading.set(false);
                this.changeDetectorRef.detectChanges();
                Tools.Utils.startFocusTrap(this.barcodeInput.nativeElement, this.changeDetectorRef);
            });
    }
}
